import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IPresentationListing } from '../../../../entities-generated/models/presentation/presentation-listing';
import { usePRIVATE_STYLES } from './style';
import { secondsToDurationString } from '../../../utils';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { toLocalDate } from '../../../../framework/utils/date-time';
import { IPRIVATE_ADDITIONAL_INFO_LABELProps, PRIVATE_ADDITIONAL_INFO_LABEL } from './added-info-label';
import { BwjPalette } from '../../../app-theme';
import { PRIVATE_CONTEXT_MENU_BUTTON } from './context-button';
import { getTheme } from '@fluentui/react';
import $ from 'jquery';
import { ListingContextMenuState } from '../../entities/listing-display-model';
import { isNotNullOrUndefined } from '../../../../framework/utils';

xlationContext('Presentation listing component');

const CONTEXT_MENU_BUTTON_WIDTH = 5.8;
const CONTEXT_MENU_SLIDE_DURATION = 300;

interface IPresentationListingProps {
  presentation: IPresentationListing;
  onWillOpenContextMenu: (presentation: IPresentationListing) => void;
  onContextMenuOpened: (presentation: IPresentationListing) => void;
  onWillCloseContextMenu: (presentation: IPresentationListing) => void;
  onContextMenuClosed: (presentation: IPresentationListing) => void;
  menuState: ListingContextMenuState;
  onEditPresentation: (presentation: IPresentationListing) => void;
  onClonePresentation: (presentation: IPresentationListing) => void;
  onBeginPresentation: (presentation: IPresentationListing) => void;
  onEndPresentation: (presentation: IPresentationListing) => void;
  onDeletePresentation: (presentation: IPresentationListing) => void;
}

const toPresentationDescription = (presentation: IPresentationListing) => {
  if(presentation.inProgress) {
    return xlate('In progress');
  } else if (isNotNullOrUndefined(presentation.endTime)) {
    const lastPresented = toLocalDate(presentation.endTime ?? 0);
    return xlate('Last presented {lastPresented:d-MMMdyyyy} @{lastPresented:d-hmmaa}', {lastPresented});
  } else {
    const lastModified = toLocalDate(presentation.lastModifiedDate);
    return xlate('Last edited {lastModified:d-MMMdyyyy} @{lastModified:d-hmmaa}', {lastModified});
  }
}

const getAdditionalInfoProps = (presentation: IPresentationListing): IPRIVATE_ADDITIONAL_INFO_LABELProps | undefined => {
  if(presentation.inProgress) {
    const endTime = toLocalDate(presentation.endTime ?? 0);
    return {
      iconColor: BwjPalette.themeSuccess,
      icon: 'StatusCircleInner',
      text: xlate('Discussion ends at {endTime:d-hmmaa}', {endTime}),
    };
  }
  
  if (presentation.hasContent === false) {
    return {
      iconColor: BwjPalette.themeWarning,
      icon: 'WarningSolid',
      text: xlate('This discussion plan has no content'),
    };
  }

  return undefined;
};

export const PresentationListingComponent: React.FunctionComponent<IPresentationListingProps> = (props) => {
  const theme = getTheme();
  const { presentation, menuState, onContextMenuOpened, onContextMenuClosed } = props;
  const styles = usePRIVATE_STYLES(presentation.inProgress, presentation.hasContent === false, CONTEXT_MENU_BUTTON_WIDTH);
  const additionalInfoProps = getAdditionalInfoProps(presentation);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const presentationRef = useRef<IPresentationListing>(presentation);
  const doubleClickCount = useRef(0);
  const startAnimationCancellationToken = useRef<number | undefined>();
  const contextMenuOpen = useMemo(() => {
    return menuState === ListingContextMenuState.Open || menuState === ListingContextMenuState.Closing;
  }, [menuState]);

  useEffect(() => { presentationRef.current = presentation; }, [presentation]);

  const openContextMenu = useCallback(() => {
    if(!contextMenuRef.current) { return; }

    const p = presentationRef.current;
    const q = $(contextMenuRef.current);
    q.animate({
      width: p.inProgress ? `${(CONTEXT_MENU_BUTTON_WIDTH * 5)}em` : `${(CONTEXT_MENU_BUTTON_WIDTH * 4)}em`
    },
    CONTEXT_MENU_SLIDE_DURATION,
    'swing',
    () => {
      onContextMenuOpened(presentationRef.current);
    });
  }, [onContextMenuOpened]);

  const closeContextMenu = useCallback(() => {
    const q = $(contextMenuRef.current!);
    q.animate({
      width: '0em'
    },
    CONTEXT_MENU_SLIDE_DURATION,
    'swing',
    () => {
      onContextMenuClosed(presentationRef.current);
    });
  }, [onContextMenuClosed]);

  useEffect(() => {
    const action = menuState === ListingContextMenuState.Closing ?
    closeContextMenu : menuState === ListingContextMenuState.Opening ?
    openContextMenu : undefined;

    if(!!action) {
      startAnimationCancellationToken.current = window.setTimeout(() => {
        action();
        startAnimationCancellationToken.current = undefined;
      }, 50);
    }
  }, [menuState, closeContextMenu, openContextMenu]);

  useEffect(() => {
    return () => {
      if(!!startAnimationCancellationToken.current) {
        window.clearTimeout(startAnimationCancellationToken.current);
      }
    }
  }, []);

  const onListingSingleClicked = () => {
    if(menuState === ListingContextMenuState.Open) {
      props.onWillCloseContextMenu(presentation);
    } else if (menuState === ListingContextMenuState.Closed) {
      props.onWillOpenContextMenu(presentation);
    }
  };

  const onListingClicked = () => {
    doubleClickCount.current++;
    if(doubleClickCount.current === 1) {
      // this will timeout the period during which we observe subsequent clicks on the listing,
      // and interpret them as all being a part of the same gesture
      window.setTimeout(() => {
        doubleClickCount.current = 0;
        onListingSingleClicked();
      }, 250);
    } else if(doubleClickCount.current === 2) {
      props.onEditPresentation(presentation);
    }
  };

  return (
    <section className={styles.listing}>
      <div className={styles.stateIndicator} />
      <div
        aria-roledescription={xlate('button')}
        className={styles.contentContainer}
        onClick={onListingClicked}
      >
        <h2 className={styles.title}>{presentation.title}</h2>
        <p className={styles.descriptionContainer}>
          <strong className={styles.duration}>{secondsToDurationString(presentation.duration)}</strong>
          <span className={styles.description}>{toPresentationDescription(presentation)}</span>
        </p>
        {!!additionalInfoProps && <PRIVATE_ADDITIONAL_INFO_LABEL {...additionalInfoProps} />}
      </div>
      <div
        ref={contextMenuRef}
        className={contextMenuOpen ? styles.contextMenuContainer_Open : styles.contextMenuContainer_Closed}
      >
        <div className={styles.contextMenuButtonContainer}>
          <PRIVATE_CONTEXT_MENU_BUTTON
            color={BwjPalette.themeError}
            icon={'Delete'}
            title={xlate('Delete discussion plan')}
            onClick={() => props.onDeletePresentation(presentation)}
          />
          <PRIVATE_CONTEXT_MENU_BUTTON
            color={theme.palette.neutralTertiary}
            icon={'Copy'}
            title={xlate('Create discussion plan copy')}
            onClick={() => props.onClonePresentation(presentation)}
          />
          <PRIVATE_CONTEXT_MENU_BUTTON
            color={theme.palette.themeTertiary}
            icon={'Edit'}
            title={xlate('Edit discussion plan')}
            onClick={() => props.onEditPresentation(presentation)}
          />
          {presentation.inProgress &&
          <PRIVATE_CONTEXT_MENU_BUTTON
            color={theme.palette.themeSecondary}
            icon={'Stop'}
            title={xlate('End discussion')}
            onClick={() => props.onEndPresentation(presentation)}
          />}
          <PRIVATE_CONTEXT_MENU_BUTTON
            color={theme.palette.themePrimary}
            icon={'Play'}
            title={presentation.inProgress ? xlate('Resume discussion') : xlate('Start discussion')}
            onClick={() => props.onBeginPresentation(presentation)}
            endButton={true}
          />
        </div>
      </div>
    </section>
  );
};
